// File: components/CutsceneView.tsx
import React, { useState, useEffect, useRef } from 'react';
import { ViewState, CutsceneScript } from '../types';
import { FastForward, Phone } from 'lucide-react';
import { drawRumi } from './renderer/player/drawRumi';
import { drawMira } from './renderer/allies/drawMira';
import { drawZoey } from './renderer/allies/drawZoey';
import { drawBobby } from './renderer/allies/drawBobby';
import { COLORS } from './config/colors';
import { ExitButton } from './ExitButton';

interface CutsceneViewProps {
  script: CutsceneScript;
  onComplete: (next: ViewState) => void;
  transparentBg?: boolean;
}

interface PortraitCanvasProps {
  characterId: string;
  isActive: boolean;
  isPhone?: boolean;
}

const getSpeakerTheme = (speaker: string) => {
  if (speaker.includes('RUMI')) return {
    plate: 'bg-purple-600 shadow-[0_0_30px_rgba(168,85,247,0.6)]',
    progress: 'bg-purple-500 shadow-[0_0_15px_#a855f7]',
    glow: 'bg-purple-500/10',
    typing: 'bg-purple-500',
    glowPos: '50%', // Rumi is Center
  };
  if (speaker.includes('MIRA')) return {
    plate: 'bg-pink-600 shadow-[0_0_30px_rgba(236,72,153,0.6)]',
    progress: 'bg-pink-500 shadow-[0_0_15px_#ec4899]',
    glow: 'bg-pink-500/10',
    typing: 'bg-pink-500',
    glowPos: '85%', // Mira is Right
  };
  if (speaker.includes('ZOEY')) return {
    plate: 'bg-cyan-600 shadow-[0_0_30px_rgba(34,211,238,0.6)]',
    progress: 'bg-cyan-400 shadow-[0_0_15px_#22d3ee]',
    glow: 'bg-cyan-500/10',
    typing: 'bg-cyan-400',
    glowPos: '15%', // Zoey is Left
  };
  if (speaker.includes('BOBBY')) return {
    plate: 'bg-blue-700 shadow-[0_0_30px_rgba(29,78,216,0.6)]',
    progress: 'bg-blue-500 shadow-[0_0_15px_#3b82f6]',
    glow: 'bg-blue-500/10',
    typing: 'bg-blue-500',
    glowPos: '50%',
  };
  if (speaker.includes('DEMON') || speaker.includes('ATTENDANT')) return {
    plate: 'bg-red-800 shadow-[0_0_30px_rgba(153,27,27,0.6)]',
    progress: 'bg-red-600 shadow-[0_0_15px_#dc2626]',
    glow: 'bg-red-500/10',
    typing: 'bg-red-600',
    glowPos: '50%',
  };
  // Default/ALL
  return {
    plate: 'bg-slate-700 shadow-[0_0_30px_rgba(255,255,255,0.2)]',
    progress: 'bg-slate-400 shadow-[0_0_15px_#94a3b8]',
    glow: 'bg-slate-500/10',
    typing: 'bg-slate-400',
    glowPos: '50%',
  };
};

const PortraitCanvas: React.FC<PortraitCanvasProps> = ({ characterId, isActive, isPhone = false }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let frameId: number;

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const centerX = canvas.width / 2;
      const centerY = isPhone ? canvas.height * 0.9 : canvas.height - 20;

      const mockPlayer = { x: 0, y: 0, width: 70, height: 180, vx: 0, isSwinging: false, swingTimer: 0, facing: 1 as (1 | -1), color: COLORS.player };
      const mockAlly = { x: 0, y: 0, width: 63, height: 162, attackCooldown: 0, targetX: 0, isAttacking: false, attackTimer: 0, color: '#fff' };

      ctx.save();
      if (characterId === 'RUMI') {
        drawRumi(ctx, { ...mockPlayer, x: centerX - 35, y: centerY - 180 });
      } else if (characterId === 'MIRA') {
        drawMira(ctx, { ...mockAlly, x: centerX - 31.5, y: centerY - 162 });
      } else if (characterId === 'ZOEY') {
        drawZoey(ctx, { ...mockAlly, x: centerX - 31.5, y: centerY - 162 });
      } else if (characterId === 'BOBBY') {
        ctx.translate(centerX, centerY);
        ctx.scale(1.2, 1.2); // Make him a bit larger to fill phone screen
        drawBobby(ctx, "BOBBY");
      }
      ctx.restore();
      frameId = requestAnimationFrame(animate);
    };

    animate();
    return () => cancelAnimationFrame(frameId);
  }, [characterId, isActive, isPhone]);

  const nonPhoneClasses = `transition-all duration-1000 transform origin-bottom ${isActive ? 'scale-150 opacity-100 translate-y-[-100px]' : 'scale-110 opacity-30 blur-[1px] grayscale translate-y-[-50px]'}`;

  return <canvas ref={canvasRef} width={400} height={600} className={isPhone ? 'w-full h-full' : nonPhoneClasses} />;
};

export const CutsceneView: React.FC<CutsceneViewProps> = ({ script, onComplete, transparentBg = false }) => {
  const [currentLineIdx, setCurrentLineIdx] = useState(0);
  const [displayText, setDisplayText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [progress, setProgress] = useState(0);

  const currentLine = script.lines[currentLineIdx];
  const physicalCast = ['ZOEY', 'RUMI', 'MIRA'];
  const isBobbyActive = currentLine.speaker.includes('BOBBY');
  const theme = getSpeakerTheme(currentLine.speaker);

  useEffect(() => {
    let i = 0;
    setDisplayText("");
    setIsTyping(true);
    setProgress(0);

    const interval = setInterval(() => {
      setDisplayText(currentLine.text.substring(0, i));
      i++;
      if (i > currentLine.text.length) {
        clearInterval(interval);
        setIsTyping(false);
      }
    }, 1); // Fastest typing speed

    return () => clearInterval(interval);
  }, [currentLineIdx, currentLine.text]);

  useEffect(() => {
    if (!isTyping) {
      const baseDelay = 50; // Drastically reduced
      const charDelay = currentLine.text.length * 2; // Drastically reduced
      const totalDelay = Math.min(500, baseDelay + charDelay); // Capped low

      const timer = setTimeout(() => {
        handleNext();
      }, totalDelay);

      const startTime = Date.now();
      const progressInterval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        setProgress(Math.min(100, (elapsed / totalDelay) * 100));
      }, 50);

      return () => {
        clearTimeout(timer);
        clearInterval(progressInterval);
      };
    }
  }, [isTyping, currentLineIdx]);

  const handleNext = () => {
    if (isTyping) {
      setIsTyping(false);
      setDisplayText(currentLine.text);
      return;
    }
    if (currentLineIdx < script.lines.length - 1) {
      setCurrentLineIdx(prev => prev + 1);
    } else {
      onComplete(script.nextView);
    }
  };

  const skip = () => onComplete(script.nextView);

  return (
    <div className={`absolute inset-0 flex flex-col items-center justify-center z-[100] overflow-hidden select-none ${!transparentBg && 'bg-black'}`}>
      {!transparentBg && <div className="absolute inset-0 bg-gradient-to-b from-purple-900/40 via-black/90 to-pink-900/40 z-0"></div>}

      <div
        className={`absolute w-[1000px] h-[1000px] rounded-full blur-[150px] transition-all duration-1000 transform -translate-y-1/2 ${theme.glow}`}
        style={{ left: theme.glowPos, opacity: isTyping ? 0.4 : 0.1 }}
      ></div>

      <div className="relative flex-1 w-full flex items-center justify-center px-4 pb-[380px] gap-2 z-10">
        {physicalCast.map(char => (
          <div key={char} className="flex-1 flex justify-center max-w-[280px]">
            <PortraitCanvas
              characterId={char}
              isActive={currentLine.speaker === 'ALL' || currentLine.speaker.includes(char) || (char === 'RUMI' && isBobbyActive)}
            />
          </div>
        ))}
      </div>

      <div
        className={`absolute z-20 transition-all duration-700 ease-in-out ${isBobbyActive ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-full'}`}
        style={{ bottom: '40%', left: '50%', transform: 'translateX(-80%)' }}
      >
        <div className="relative w-[280px] h-[580px] origin-bottom-right transition-transform duration-500" style={{ transform: `rotate(15deg) scale(${isBobbyActive ? 1 : 0.8})` }}>
          {/* Hand SVG */}
          <svg viewBox="0 0 100 100" className="absolute -bottom-16 -left-20 w-48 h-48 pointer-events-none drop-shadow-2xl">
            <path d="M65,95 C45,105 30,80 20,60 S10,20 30,10 C50,0 70,20 75,40 L85,60 C90,70 85,85 65,95 Z" fill="#f3d2b3" />
            <path d="M22,58 C12,50 10,35 20,25" stroke="#d1b392" fill="none" strokeWidth="2" />
            <path d="M76,42 C80,48 82,58 78,65 L70,80" stroke="#d1b392" fill="none" strokeWidth="2" />
            {/* Nails */}
            <path d="M16,24 A8,8 0 0,1 24,18" fill="#1a1311" />
            <path d="M12,34 A8,8 0 0,1 20,28" fill="#1a1311" />
            <path d="M10,48 A8,8 0 0,1 18,42" fill="#1a1311" />
          </svg>
          <div className="relative w-full h-full bg-slate-900 rounded-[40px] border-4 border-slate-700 shadow-2xl p-2.5">
            <div className="bg-black w-full h-full rounded-[30px] overflow-hidden relative">
              <div className="absolute top-2 left-1/2 -translate-x-1/2 w-16 h-4 bg-slate-900 rounded-full z-10"></div>
              <PortraitCanvas characterId={'BOBBY'} isActive={true} isPhone={true} />
            </div>
          </div>
        </div>
      </div>

      <div
        className="absolute bottom-0 left-0 right-0 w-full glass-panel p-8 z-50 border-white/10 transition-all duration-500 bg-black/80 shadow-[0_-30px_60px_rgba(0,0,0,0.9)] rounded-none"
        style={{ minHeight: '220px', cursor: 'pointer' }}
        onClick={handleNext}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className={`px-10 py-2 transition-colors duration-300 ${theme.plate} text-white font-heading text-xl tracking-[0.4em] rounded-sm border border-white/20`}>
              {currentLine.speaker}
            </div>
            {isBobbyActive && <Phone size={20} className="text-blue-400 animate-pulse" />}
          </div>
        </div>

        <p className="text-4xl md:text-5xl lg:text-6xl text-white leading-[1.15] font-heading font-medium tracking-tight drop-shadow-[0_4px_10px_rgba(0,0,0,1)]">
          {displayText}
          {isTyping && <span className={`inline-block w-3 h-12 ml-4 animate-pulse transition-colors duration-300 ${theme.typing}`}></span>}
        </p>

        <div className="absolute bottom-0 left-0 w-full h-1.5 bg-white/5 overflow-hidden rounded-b-lg">
          <div
            className={`h-full transition-all duration-100 ease-linear ${theme.progress}`}
            style={{ width: `${progress}%`, opacity: isTyping ? 0 : 1 }}
          ></div>
        </div>
      </div>

      <ExitButton onExit={skip} label="SKIP" icon={FastForward} />

      <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-black to-transparent z-40"></div>
      <div className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-black to-transparent z-40"></div>
    </div>
  );
};